import { db } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { notificationService } from './notificationService';
import { rskService } from './rskService';

/**
 * Progress Tracking Service
 * 
 * Firestore Schema:
 * user_progress/{userId}/courses/{courseId}
 * {
 *   courseId: string,
 *   enrolledAt: timestamp,
 *   lastAccessedAt: timestamp,
 *   overallProgress: number (0-100),
 *   completedModules: number,
 *   totalModules: number,
 *   modules: {
 *     [moduleId]: {
 *       completed: boolean,
 *       completedAt: timestamp,
 *       videoProgress: number (0-100),
 *       videoCompleted: boolean,
 *       widgetCompleted: boolean,
 *       required: boolean,
 *       lastAccessedAt: timestamp
 *     }
 *   }
 * }
 */

export const progressService = {
  // Enroll user in a course
  enrollInCourse: async (userId, courseId, courseData) => {
    try {
      const progressRef = doc(db, 'user_progress', userId, 'courses', courseId);
      const progressSnap = await getDoc(progressRef);

      if (progressSnap.exists()) {
        // Already enrolled, just update last accessed
        await updateDoc(progressRef, {
          lastAccessedAt: new Date(),
        });
        return { success: true, error: null };
      }

      // Calculate total modules
      let totalModules = 0;
      const modulesData = {};
      
      if (courseData.chapters) {
        courseData.chapters.forEach(chapter => {
          if (chapter.modules) {
            chapter.modules.forEach(module => {
              totalModules++;
              modulesData[module.id] = {
                completed: false,
                completedAt: null,
                videoProgress: 0,
                videoCompleted: false,
                widgetCompleted: module.widgetId ? false : true, // If no widget, mark as completed
                required: module.required || false,
                lastAccessedAt: null,
              };
            });
          }
        });
      }

      // Get user commitment level for metrics
      let userCommitmentLevel = null;
      try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          userCommitmentLevel = userSnap.data().commitmentLevel || null;
        }
      } catch (error) {
        // Silently fail - commitment level is optional for metrics
        console.log('Could not fetch user commitment level:', error.message);
      }

      // Create enrollment record
      const enrollmentData = {
        courseId,
        enrolledAt: new Date(),
        lastAccessedAt: new Date(),
        overallProgress: 0,
        completedModules: 0,
        totalModules,
        modules: modulesData,
        // Store commitment level for metrics tracking
        userCommitmentLevel,
      };

      await setDoc(progressRef, enrollmentData);

      // Create deadline notifications if enabled
      if (courseData.deadlines?.enabled) {
        await notificationService.createCourseDeadlineNotifications(userId, courseData);
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error enrolling in course:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user's progress for a specific course
  getCourseProgress: async (userId, courseId) => {
    try {
      const progressRef = doc(db, 'user_progress', userId, 'courses', courseId);
      const progressSnap = await getDoc(progressRef);

      if (progressSnap.exists()) {
        return { progress: progressSnap.data(), error: null };
      } else {
        return { progress: null, error: null };
      }
    } catch (error) {
      console.error('Error fetching course progress:', error);
      return { progress: null, error: error.message };
    }
  },

  // Get all courses user is enrolled in
  getAllUserProgress: async (userId) => {
    try {
      const coursesRef = collection(db, 'user_progress', userId, 'courses');
      const coursesSnap = await getDocs(coursesRef);
      const courses = [];

      coursesSnap.forEach((doc) => {
        courses.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { courses, error: null };
    } catch (error) {
      console.error('Error fetching user progress:', error);
      return { courses: [], error: error.message };
    }
  },

  // Update video progress for a module
  updateVideoProgress: async (userId, courseId, moduleId, progressPercent) => {
    try {
      const progressRef = doc(db, 'user_progress', userId, 'courses', courseId);
      const progressSnap = await getDoc(progressRef);

      if (!progressSnap.exists()) {
        return { success: false, error: 'Not enrolled in this course' };
      }

      const progressData = progressSnap.data();
      const modules = progressData.modules || {};
      
      // Update module video progress
      if (!modules[moduleId]) {
        modules[moduleId] = {
          completed: false,
          completedAt: null,
          videoProgress: 0,
          videoCompleted: false,
          widgetCompleted: true,
          required: false,
          lastAccessedAt: new Date(),
        };
      }

      modules[moduleId].videoProgress = progressPercent;
      modules[moduleId].lastAccessedAt = new Date();
      
      // Mark as completed if >= 90%
      if (progressPercent >= 90) {
        modules[moduleId].videoCompleted = true;
      }

      // Check if module is fully completed
      const isModuleComplete = modules[moduleId].videoCompleted && modules[moduleId].widgetCompleted;
      if (isModuleComplete && !modules[moduleId].completed) {
        modules[moduleId].completed = true;
        modules[moduleId].completedAt = new Date();
      }

      // Recalculate overall progress
      const completedModules = Object.values(modules).filter(m => m.completed).length;
      const overallProgress = progressData.totalModules > 0 
        ? Math.round((completedModules / progressData.totalModules) * 100)
        : 0;

      await updateDoc(progressRef, {
        modules,
        completedModules,
        overallProgress,
        lastAccessedAt: new Date(),
      });

      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating video progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Mark widget as completed
  completeWidget: async (userId, courseId, moduleId) => {
    try {
      const progressRef = doc(db, 'user_progress', userId, 'courses', courseId);
      const progressSnap = await getDoc(progressRef);

      if (!progressSnap.exists()) {
        return { success: false, error: 'Not enrolled in this course' };
      }

      const progressData = progressSnap.data();
      const modules = progressData.modules || {};

      if (!modules[moduleId]) {
        return { success: false, error: 'Module not found' };
      }

      modules[moduleId].widgetCompleted = true;
      modules[moduleId].lastAccessedAt = new Date();

      // Check if module is fully completed
      const isModuleComplete = modules[moduleId].videoCompleted && modules[moduleId].widgetCompleted;
      if (isModuleComplete && !modules[moduleId].completed) {
        modules[moduleId].completed = true;
        modules[moduleId].completedAt = new Date();
      }

      // Recalculate overall progress
      const completedModules = Object.values(modules).filter(m => m.completed).length;
      const overallProgress = progressData.totalModules > 0 
        ? Math.round((completedModules / progressData.totalModules) * 100)
        : 0;

      await updateDoc(progressRef, {
        modules,
        completedModules,
        overallProgress,
        lastAccessedAt: new Date(),
      });

      return { success: true, error: null };
    } catch (error) {
      console.error('Error completing widget:', error);
      return { success: false, error: error.message };
    }
  },

  // Manually mark module as completed (for non-video/widget content)
  completeModule: async (userId, courseId, moduleId, moduleTitle = 'Module') => {
    try {
      const progressRef = doc(db, 'user_progress', userId, 'courses', courseId);
      const progressSnap = await getDoc(progressRef);

      if (!progressSnap.exists()) {
        return { success: false, error: 'Not enrolled in this course' };
      }

      const progressData = progressSnap.data();
      const modules = progressData.modules || {};
      const wasAlreadyCompleted = modules[moduleId]?.completed || false;

      if (!modules[moduleId]) {
        modules[moduleId] = {
          completed: false,
          completedAt: null,
          videoProgress: 100,
          videoCompleted: true,
          widgetCompleted: true,
          required: false,
          lastAccessedAt: new Date(),
        };
      }

      modules[moduleId].completed = true;
      modules[moduleId].completedAt = new Date();
      modules[moduleId].videoCompleted = true;
      modules[moduleId].widgetCompleted = true;
      modules[moduleId].lastAccessedAt = new Date();

      // Recalculate overall progress
      const completedModules = Object.values(modules).filter(m => m.completed).length;
      const overallProgress = progressData.totalModules > 0 
        ? Math.round((completedModules / progressData.totalModules) * 100)
        : 0;

      await updateDoc(progressRef, {
        modules,
        completedModules,
        overallProgress,
        lastAccessedAt: new Date(),
      });

      // Award RSK for module completion (only if it's the first time completing this module)
      if (!wasAlreadyCompleted) {
        try {
          const result = await rskService.awardModuleCompletion(userId, moduleTitle);
        } catch (rskError) {
          console.error('Error awarding RSK for module completion:', rskError);
          // Don't fail the whole operation if RSK award fails
        }
      } else {
        console.log(`[Progress Service] Module ${moduleId} already completed, no RSK awarded`);
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error completing module:', error);
      return { success: false, error: error.message };
    }
  },

  // Check if user can access next module (based on required modules)
  canAccessModule: async (userId, courseId, moduleId, courseData) => {
    try {
      const { progress, error } = await progressService.getCourseProgress(userId, courseId);
      
      if (error || !progress) {
        // Not enrolled, can access first module only
        return { canAccess: false, reason: 'Not enrolled in course' };
      }

      // Find the module and its prerequisites
      let moduleFound = false;
      let requiredModulesCompleted = true;
      let missingModules = [];

      if (courseData.chapters) {
        for (const chapter of courseData.chapters) {
          if (chapter.modules) {
            for (const module of chapter.modules) {
              if (module.id === moduleId) {
                moduleFound = true;
                break;
              }
              
              // Check if any previous required modules are incomplete
              if (module.required && !moduleFound) {
                const moduleProgress = progress.modules[module.id];
                if (!moduleProgress || !moduleProgress.completed) {
                  requiredModulesCompleted = false;
                  missingModules.push(module.title || 'Untitled Module');
                }
              }
            }
            if (moduleFound) break;
          }
        }
      }

      if (!moduleFound) {
        return { canAccess: false, reason: 'Module not found' };
      }

      if (!requiredModulesCompleted) {
        return { 
          canAccess: false, 
          reason: `Complete required modules first: ${missingModules.join(', ')}` 
        };
      }

      return { canAccess: true, reason: null };
    } catch (error) {
      console.error('Error checking module access:', error);
      return { canAccess: false, reason: error.message };
    }
  },
};

export default progressService;

